import React from 'react';
import { Combine, Eye, Minus, Plus, Redo2, RotateCcw, Ruler, Settings2, Shrink, Trash2, Undo2 } from 'lucide-react';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { createDefaultDocumentLayout } from '../../utils/defaultDocumentLayout';
import {
  LAYOUT_FIELD_DEFS,
  PDF_A4_WIDTH_PT,
  isLabeledFieldKey,
  newLayoutElementId,
  stripLegacyPaymentQrElements,
  type DocumentLayoutTemplateRow,
  type DocumentLayoutV1,
  type LayoutBand,
  type LayoutElement,
  type LayoutElementKey,
} from '../../utils/documentLayoutTypes';
import {
  canRemoveElement,
  isMandatoryElementKey,
  validateDocumentLayout,
} from '../../utils/documentLayoutValidation';
import {
  documentLayoutAssetSrc,
  deleteDocumentLayoutTemplate,
  updateDocumentLayoutTemplate,
  uploadDocumentLayoutAsset,
} from '../../utils/documentLayoutApi';
import { SETTINGS_BTN_DANGER_OUTLINE, SETTINGS_BTN_OUTLINE, SETTINGS_BTN_PRIMARY } from '../../utils/settingsSectionStyles';
import {
  companyLogoSrc,
  instanceIdFromPathname,
} from '../../../../utils/companyLogo';
import { LayoutCanvas } from './LayoutCanvas';
import { LayoutInspector } from './LayoutInspector';
import { LayoutPalette } from './LayoutPalette';
import { PageSettingsPanel } from './PageSettingsPanel';
import type { CompanySettings } from '../../../../types';
import {
  buildLayoutSampleData,
  expandLayoutToFitContent,
  findElementBand,
  LAYOUT_SAMPLE_DATA,
  mergeLabeledSelectionToFreeText,
  removeElementFromLayout,
  roundLayoutGeometry,
  snapRecipientToDinAddressWindow,
  updateBandHeight,
  updateElementInLayout,
  type LayoutSampleData,
} from './layoutEditorUtils';
import { mapDocumentNumberFormatSettings } from '../../../../utils/documentNumberFormat';

/** In-editor clipboard for duplicating Freitext via Ctrl/Cmd+C / Ctrl/Cmd+V. */
type LayoutClipboard = {
  element: LayoutElement;
  band: LayoutBand;
};

function cloneOffsetElement(el: LayoutElement, bandHeight: number): LayoutElement {
  return {
    ...structuredClone(el),
    id: newLayoutElementId(el.key),
    xPt: Math.min(PDF_A4_WIDTH_PT - el.wPt, el.xPt + 12),
    yPt: Math.min(bandHeight - el.hPt, el.yPt + 12),
    locked: false,
  };
}

type Props = {
  template: DocumentLayoutTemplateRow;
  onSaved: (tpl: DocumentLayoutTemplateRow) => void;
  onDeleted?: () => void;
};

/**
 * Elements outside their band cannot be edited sensibly; such layouts restart from the default.
 * Overlaps are allowed on purpose and never trigger a reset.
 */
function layoutHasElementsOutOfBounds(layout: DocumentLayoutV1): boolean {
  return validateDocumentLayout(layout).issues.some((i) => i.code === 'out_of_bounds');
}

/** Pflichtfelder were incorrectly auto-locked; unlock them so they stay movable. */
function unlockMandatoryElements(layout: DocumentLayoutV1): DocumentLayoutV1 {
  const next = structuredClone(layout);
  for (const e of next.header.elements) {
    if (isMandatoryElementKey(e.key, 'header')) e.locked = false;
  }
  for (const e of next.footer.elements) {
    if (isMandatoryElementKey(e.key, 'footer')) e.locked = false;
  }
  return next;
}

function initialLayoutFromTemplate(template: DocumentLayoutTemplateRow): DocumentLayoutV1 {
  const cloned = stripLegacyPaymentQrElements(structuredClone(template.layout));
  if (layoutHasElementsOutOfBounds(cloned)) {
    return roundLayoutGeometry(expandLayoutToFitContent(createDefaultDocumentLayout()));
  }
  // Keep stored geometry as-is so existing templates (e.g. ND) do not reflow on open/save.
  return roundLayoutGeometry(unlockMandatoryElements(cloned));
}

export function DocumentLayoutEditor({ template, onSaved, onDeleted }: Props) {
  const bootLayout = React.useMemo(() => initialLayoutFromTemplate(template), [template]);
  const [name, setName] = React.useState(template.name);
  const [layout, setLayout] = React.useState<DocumentLayoutV1>(() => bootLayout);
  const [history, setHistory] = React.useState<DocumentLayoutV1[]>([structuredClone(bootLayout)]);
  const [historyIndex, setHistoryIndex] = React.useState(0);
  const historyIndexRef = React.useRef(0);
  React.useEffect(() => {
    historyIndexRef.current = historyIndex;
  }, [historyIndex]);
  const [activeBand, setActiveBand] = React.useState<LayoutBand>('header');
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [multiSelectIds, setMultiSelectIds] = React.useState<string[]>([]);
  const layoutClipboardRef = React.useRef<LayoutClipboard | null>(null);
  const [zoom, setZoom] = React.useState(1);
  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 4;
  const ZOOM_STEP = 0.05;

  const nudgeZoom = React.useCallback((delta: number) => {
    setZoom((z) => {
      const next = Math.round((z + delta) * 100) / 100;
      return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next));
    });
  }, []);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key === '=' || e.key === '+') {
        e.preventDefault();
        nudgeZoom(ZOOM_STEP);
      } else if (e.key === '-') {
        e.preventDefault();
        nudgeZoom(-ZOOM_STEP);
      } else if (e.key === '0') {
        e.preventDefault();
        setZoom(1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nudgeZoom]);
  const [showGuides, setShowGuides] = React.useState(true);
  const [showPageSettings, setShowPageSettings] = React.useState(false);
  const [previewMode, setPreviewMode] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [savedSnapshot, setSavedSnapshot] = React.useState(() => ({
    name: template.name,
    layout: structuredClone(bootLayout),
  }));
  const [showSavedHint, setShowSavedHint] = React.useState(false);
  const isDirty =
    name.trim() !== savedSnapshot.name.trim() ||
    JSON.stringify(layout) !== JSON.stringify(savedSnapshot.layout);
  React.useEffect(() => {
    if (isDirty) setShowSavedHint(false);
  }, [isDirty]);
  const [logoUrl, setLogoUrl] = React.useState<string | null>(null);
  const [sampleData, setSampleData] = React.useState<LayoutSampleData>(LAYOUT_SAMPLE_DATA);
  const [introParas, setIntroParas] = React.useState<string[]>([]);
  const [assetBust, setAssetBust] = React.useState(() => Date.now());
  const [assetUploading, setAssetUploading] = React.useState(false);
  const [contextMenu, setContextMenu] = React.useState<{
    id: string;
    x: number;
    y: number;
  } | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    const instanceId = instanceIdFromPathname(window.location.pathname);
    const src = companyLogoSrc(instanceId, Date.now());
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setLogoUrl(src);
    };
    img.onerror = () => {
      if (!cancelled) setLogoUrl(null);
    };
    img.src = src;

    (async () => {
      try {
        const [coRes, meRes, numRes, textsRes] = await Promise.all([
          fetch('/api/settings/company', { credentials: 'include' }),
          fetch('/api/me', { credentials: 'include' }),
          fetch('/api/settings/document-numbering', { credentials: 'include' }),
          fetch('/api/settings/document-texts?type=Rechnung', { credentials: 'include' }),
        ]);
        let company: CompanySettings | null = null;
        let userName = '';
        let userEmail = '';
        let documentNumberFormat = null as ReturnType<typeof mapDocumentNumberFormatSettings> | null;
        if (coRes.ok) {
          company = (await coRes.json()) as CompanySettings;
        }
        if (meRes.ok) {
          const me = (await meRes.json()) as { user?: { username?: string; email?: string } };
          userName = String(me.user?.username ?? '').trim();
          userEmail = String(me.user?.email ?? '').trim();
        }
        if (numRes.ok) {
          const numJson = (await numRes.json()) as Record<string, unknown>;
          documentNumberFormat = mapDocumentNumberFormatSettings(numJson);
        }
        if (textsRes.ok) {
          const texts = (await textsRes.json()) as { introLetterParas?: unknown };
          if (!cancelled) {
            setIntroParas(Array.isArray(texts.introLetterParas) ? texts.introLetterParas.map(String) : []);
          }
        } else if (!cancelled) {
          setIntroParas([]);
        }
        if (!cancelled) {
          setSampleData(
            buildLayoutSampleData({ company, userName, userEmail, documentNumberFormat }),
          );
        }
      } catch {
        // Keep LAYOUT_SAMPLE_DATA fallback.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!previewMode) return;
    let cancelled = false;
    fetch('/api/settings/document-texts?type=Rechnung', { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) return;
        const texts = (await res.json()) as { introLetterParas?: unknown };
        if (cancelled) return;
        setIntroParas(Array.isArray(texts.introLetterParas) ? texts.introLetterParas.map(String) : []);
      })
      .catch(() => {
        /* keep last intro */
      });
    return () => {
      cancelled = true;
    };
  }, [previewMode]);

  const commit = React.useCallback((raw: DocumentLayoutV1) => {
    const next = roundLayoutGeometry(raw);
    setLayout(next);
    setHistory((prev) => {
      const trimmed = prev.slice(0, historyIndexRef.current + 1);
      trimmed.push(structuredClone(next));
      if (trimmed.length > 60) trimmed.shift();
      historyIndexRef.current = trimmed.length - 1;
      setHistoryIndex(historyIndexRef.current);
      return trimmed;
    });
  }, []);

  const undo = () => {
    if (historyIndex <= 0) return;
    const nextIdx = historyIndex - 1;
    setHistoryIndex(nextIdx);
    setLayout(structuredClone(history[nextIdx]));
    setSelectedId(null);
  };

  const redo = () => {
    if (historyIndex >= history.length - 1) return;
    const nextIdx = historyIndex + 1;
    setHistoryIndex(nextIdx);
    setLayout(structuredClone(history[nextIdx]));
    setSelectedId(null);
  };

  const selectedBand = selectedId ? findElementBand(layout, selectedId) : null;
  const selectedEl =
    selectedBand && selectedId
      ? (selectedBand === 'header' ? layout.header.elements : layout.footer.elements).find(
          (e) => e.id === selectedId,
        ) ?? null
      : null;

  const assetUrls = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const el of [...layout.header.elements, ...layout.footer.elements]) {
      const path = (el.imageObjectPath ?? '').trim();
      if (
        path &&
        (el.key === 'headerBanner' || el.key === 'signatureImage')
      ) {
        map[el.id] = documentLayoutAssetSrc(path, assetBust);
      }
    }
    return map;
  }, [layout, assetBust]);

  const patchSelected = (patch: Partial<LayoutElement>) => {
    if (!selectedId || !selectedBand) return;
    commit(updateElementInLayout(layout, selectedBand, selectedId, patch));
  };

  const commitBandElements = (band: LayoutBand, elements: LayoutElement[], opts?: { recordHistory?: boolean }) => {
    setLayout((prev) => {
      const draft = structuredClone(prev);
      if (band === 'header') draft.header.elements = elements;
      else draft.footer.elements = elements;
      const next = roundLayoutGeometry(draft);
      if (opts?.recordHistory !== false) {
        setHistory((hist) => {
          const trimmed = hist.slice(0, historyIndexRef.current + 1);
          trimmed.push(structuredClone(next));
          if (trimmed.length > 60) trimmed.shift();
          historyIndexRef.current = trimmed.length - 1;
          setHistoryIndex(historyIndexRef.current);
          return trimmed;
        });
      }
      return next;
    });
  };

  const commitBandHeight = (
    band: LayoutBand,
    heightPt: number,
    opts?: { recordHistory?: boolean },
  ) => {
    setLayout((prev) => {
      const next = roundLayoutGeometry(updateBandHeight(prev, band, heightPt));
      if (opts?.recordHistory !== false) {
        setHistory((hist) => {
          const trimmed = hist.slice(0, historyIndexRef.current + 1);
          trimmed.push(structuredClone(next));
          if (trimmed.length > 60) trimmed.shift();
          historyIndexRef.current = trimmed.length - 1;
          setHistoryIndex(historyIndexRef.current);
          return trimmed;
        });
      }
      return next;
    });
  };

  const onDropPalette = (
    payload: { key: string; band: LayoutBand; lineOrientation?: 'horizontal' | 'vertical' },
    xPt: number,
    yPt: number,
  ) => {
    const def = LAYOUT_FIELD_DEFS.find((d) => d.key === payload.key && d.band === payload.band);
    if (!def) return;
    const band = payload.band;
    setActiveBand(band);
    const bandHeight = band === 'header' ? layout.header.heightPt : layout.footer.heightPt;
    const verticalLine = payload.key === 'divider' && payload.lineOrientation === 'vertical';
    const w = verticalLine ? 2 : def.defaultSize.wPt;
    const h = verticalLine ? Math.min(120, Math.max(24, bandHeight - 8)) : def.defaultSize.hPt;
    const el: LayoutElement = {
      id: newLayoutElementId(def.key),
      key: def.key as LayoutElementKey,
      xPt: Math.max(0, Math.min(PDF_A4_WIDTH_PT - w, xPt)),
      yPt: Math.max(0, Math.min(bandHeight - h, yPt)),
      wPt: w,
      hPt: h,
      align: 'left',
      locked: false,
      ...(verticalLine ? { lineOrientation: 'vertical' as const } : {}),
    };
    const next = structuredClone(layout);
    if (band === 'header') next.header.elements.push(el);
    else next.footer.elements.push(el);
    commit(next);
    setSelectedId(el.id);
  };

  const onSave = async () => {
    if (!isDirty || saving || deleting) return;
    setError(null);
    const validation = validateDocumentLayout(layout);
    if (template.is_default && !validation.ok) {
      const details = validation.issues.map((i) => i.message).filter(Boolean);
      setError(
        details.length
          ? `Die Standard-Vorlage muss gültig sein: ${details.join(' · ')}`
          : 'Die Standard-Vorlage muss gültig sein. Bitte Fehler beheben oder zuerst eine andere Vorlage als Standard setzen.',
      );
      return;
    }
    setSaving(true);
    try {
      const savedName = name.trim() || template.name;
      const saved = await updateDocumentLayoutTemplate(template.id, {
        name: savedName,
        layout,
      });
      setError(null);
      setSavedSnapshot({
        name: saved.name,
        layout: structuredClone(layout),
      });
      setShowSavedHint(true);
      onSaved(saved);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    const label = name.trim() || template.name;
    const confirmMsg = template.is_default
      ? `Vorlage „${label}“ wirklich löschen?\n\nOhne Standard-Vorlage gilt wieder das native Dokument-Layout.`
      : `Vorlage „${label}“ wirklich löschen?`;
    if (!window.confirm(confirmMsg)) return;
    setError(null);
    setDeleting(true);
    try {
      await deleteDocumentLayoutTemplate(template.id);
      onDeleted?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen.');
    } finally {
      setDeleting(false);
    }
  };

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      const editable =
        (e.target as HTMLElement)?.isContentEditable || tag === 'input' || tag === 'textarea';
      if ((e.ctrlKey || e.metaKey) && !editable) {
        if (e.key === 'z' && !e.shiftKey) {
          e.preventDefault();
          undo();
          return;
        }
        if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
          e.preventDefault();
          redo();
          return;
        }
        if (e.key === 's') {
          e.preventDefault();
          void onSave();
          return;
        }
        if (e.key === 'c' || e.key === 'C') {
          if (!selectedEl || selectedEl.key !== 'freeText') return;
          const band = findElementBand(layout, selectedEl.id);
          if (!band) return;
          e.preventDefault();
          layoutClipboardRef.current = {
            element: structuredClone(selectedEl),
            band,
          };
          return;
        }
        if (e.key === 'v' || e.key === 'V') {
          const clip = layoutClipboardRef.current;
          if (!clip || clip.element.key !== 'freeText') return;
          e.preventDefault();
          // Paste into the active band when Freitext is allowed there; else keep source band.
          const targetBand: LayoutBand =
            LAYOUT_FIELD_DEFS.some((d) => d.key === 'freeText' && d.band === activeBand)
              ? activeBand
              : clip.band;
          const bandHeight =
            targetBand === 'header' ? layout.header.heightPt : layout.footer.heightPt;
          const copy = cloneOffsetElement(clip.element, bandHeight);
          const next = structuredClone(layout);
          if (targetBand === 'header') next.header.elements.push(copy);
          else next.footer.elements.push(copy);
          commit(next);
          setActiveBand(targetBand);
          setSelectedId(copy.id);
          setMultiSelectIds([]);
          return;
        }
      }
      if ((!selectedId && multiSelectIds.length === 0) || editable) return;

      const activeIds =
        multiSelectIds.length > 0 ? multiSelectIds : selectedId ? [selectedId] : [];

      if (e.key === 'Delete' || e.key === 'Backspace') {
        let next = layout;
        let removed = false;
        for (const id of activeIds) {
          const band = findElementBand(next, id);
          if (!band || !canRemoveElement(next, band, id)) continue;
          next = removeElementFromLayout(next, band, id);
          removed = true;
        }
        if (!removed) return;
        e.preventDefault();
        commit(next);
        setSelectedId(null);
        setMultiSelectIds([]);
        return;
      }

      const step = e.shiftKey ? 10 : 1;
      let dx = 0;
      let dy = 0;
      if (e.key === 'ArrowLeft') dx = -step;
      else if (e.key === 'ArrowRight') dx = step;
      else if (e.key === 'ArrowUp') dy = -step;
      else if (e.key === 'ArrowDown') dy = step;
      else return;
      e.preventDefault();

      const next = structuredClone(layout);
      let moved = false;
      for (const id of activeIds) {
        const band = findElementBand(layout, id);
        if (!band) continue;
        const list = band === 'header' ? next.header.elements : next.footer.elements;
        const idx = list.findIndex((el) => el.id === id);
        if (idx < 0) continue;
        const el = list[idx];
        if (el.locked) continue;
        const bandHeight = band === 'header' ? layout.header.heightPt : layout.footer.heightPt;
        list[idx] = {
          ...el,
          xPt: Math.max(0, Math.min(PDF_A4_WIDTH_PT - el.wPt, el.xPt + dx)),
          yPt: Math.max(0, Math.min(bandHeight - el.hPt, el.yPt + dy)),
        };
        moved = true;
      }
      if (moved) commit(next);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selectedEl, multiSelectIds, layout, historyIndex, activeBand, isDirty, saving, deleting]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;
  const layoutValidation = React.useMemo(() => validateDocumentLayout(layout), [layout]);
  const validationWarning = !layoutValidation.ok
    ? template.is_default
      ? 'Layout hat Fehler — als Standard-Vorlage erst speicherbar, wenn alles gültig ist.'
      : 'Layout hat noch Fehler — speichern möglich, als Standard erst nach Korrektur.'
    : null;
  const multiSelectCount = multiSelectIds.length;
  const canMergeLabeled = React.useMemo(() => {
    if (multiSelectCount < 2) return false;
    const items = multiSelectIds.map((id) => {
      const band = findElementBand(layout, id);
      if (!band) return null;
      const el = (band === 'header' ? layout.header.elements : layout.footer.elements).find(
        (e) => e.id === id,
      );
      if (!el || el.locked || !isLabeledFieldKey(el.key)) return null;
      return { el, band };
    });
    if (items.some((x) => !x)) return false;
    const band = items[0]!.band;
    return items.every((x) => x!.band === band);
  }, [layout.footer.elements, layout.header.elements, multiSelectCount, multiSelectIds]);

  const mergeSelection = () => {
    const result = mergeLabeledSelectionToFreeText(layout, multiSelectIds);
    if (!result) {
      setError(
        'Zusammenführen nicht möglich — bitte zwei Label-Felder im selben Bereich wählen (z. B. Leistungsort + Leistungszeitraum).',
      );
      return;
    }
    setError(null);
    commit(result.layout);
    setMultiSelectIds([]);
    setSelectedId(result.newId);
  };

  const iconBtn = (active?: boolean) =>
    `inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors disabled:pointer-events-none disabled:opacity-35 ${
      active
        ? 'bg-blue-50 text-pipedrive-blue'
        : 'text-app-text-subtle hover:bg-app-hover hover:text-app-text'
    }`;

  const toolBtn = (active?: boolean) =>
    `inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors ${
      active
        ? 'bg-blue-50 text-pipedrive-blue'
        : 'text-app-text-muted hover:bg-app-hover hover:text-app-text'
    }`;

  return (
    <div className="theme-light flex h-full min-h-0 flex-col bg-app-bg">
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <LayoutPalette
          layout={layout}
          activeBand={activeBand}
          selectedId={selectedId}
          templateName={name}
          onTemplateNameChange={setName}
          onBandChange={setActiveBand}
        />

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div className="flex h-12 shrink-0 items-center gap-3 overflow-x-auto border-b border-app-border bg-app-surface px-3">
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                className={iconBtn()}
                onClick={undo}
                disabled={!canUndo}
                title="Rückgängig (Strg+Z)"
                aria-label="Rückgängig"
              >
                <Undo2 className="h-4 w-4" />
              </button>
              <button
                type="button"
                className={iconBtn()}
                onClick={redo}
                disabled={!canRedo}
                title="Wiederholen (Strg+Y)"
                aria-label="Wiederholen"
              >
                <Redo2 className="h-4 w-4" />
              </button>
            </div>

            <div className="h-5 w-px shrink-0 bg-slate-200" />

            <div
              className="flex h-8 items-center overflow-hidden rounded-md border border-app-border bg-app-surface-muted/80"
              title="Strg + Mausrad zum Zoomen"
            >
              <button
                type="button"
                className="inline-flex h-full w-8 items-center justify-center text-app-text-subtle transition-colors hover:bg-app-surface hover:text-app-text disabled:opacity-35"
                onClick={() => nudgeZoom(-ZOOM_STEP)}
                disabled={zoom <= ZOOM_MIN}
                aria-label="Zoom verkleinern"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                className="h-full min-w-[3.25rem] border-x border-app-border bg-app-surface px-2 text-xs font-medium tabular-nums text-app-text-muted transition-colors hover:bg-app-hover"
                onClick={() => setZoom(1)}
                title="Auf 100 % zurücksetzen"
                aria-label={`Zoom ${Math.round(zoom * 100)} Prozent, Klick setzt auf 100 Prozent`}
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                type="button"
                className="inline-flex h-full w-8 items-center justify-center text-app-text-subtle transition-colors hover:bg-app-surface hover:text-app-text disabled:opacity-35"
                onClick={() => nudgeZoom(ZOOM_STEP)}
                disabled={zoom >= ZOOM_MAX}
                aria-label="Zoom vergrößern"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="h-5 w-px shrink-0 bg-slate-200" />

            {multiSelectCount >= 2 && canMergeLabeled ? (
              <>
                <div className="flex items-center gap-0.5">
                  <button
                    type="button"
                    className={toolBtn()}
                    onClick={mergeSelection}
                    title="Markierte Label-Felder zu einer Zeile zusammenführen (mit •)"
                    aria-label="Zusammenführen"
                  >
                    <Combine className="h-3.5 w-3.5" />
                    Zusammenführen
                  </button>
                </div>
                <div className="h-5 w-px shrink-0 bg-slate-200" />
              </>
            ) : null}

            <div className="flex items-center gap-0.5">
              <button
                type="button"
                className={toolBtn(showPageSettings)}
                onClick={() => {
                  setShowPageSettings((v) => {
                    if (!v) {
                      setSelectedId(null);
                      setMultiSelectIds([]);
                    }
                    return !v;
                  });
                }}
                aria-pressed={showPageSettings}
                title="Seiteneinstellungen bearbeiten"
              >
                <Settings2 className="h-3.5 w-3.5" />
                Seiteneinstellungen
              </button>
              <button
                type="button"
                className={toolBtn(showGuides)}
                onClick={() => setShowGuides((v) => !v)}
                aria-pressed={showGuides}
                title="Hilfslinien ein-/ausblenden"
              >
                <Ruler className="h-3.5 w-3.5" />
                Hilfslinien
              </button>
              <button
                type="button"
                className={toolBtn(previewMode)}
                onClick={() => {
                  setPreviewMode((v) => !v);
                  setSelectedId(null);
                  setMultiSelectIds([]);
                }}
                aria-pressed={previewMode}
                title="Vorschau ohne Bearbeitung"
              >
                <Eye className="h-3.5 w-3.5" />
                Vorschau
              </button>
              <button
                type="button"
                className={toolBtn()}
                onClick={() => commit(expandLayoutToFitContent(layout))}
                title="Feld- und Bandhöhen auf den Inhalt zusammenziehen"
              >
                <Shrink className="h-3.5 w-3.5" />
                Höhe anpassen
              </button>
              <button
                type="button"
                className={toolBtn()}
                onClick={() => {
                  commit(expandLayoutToFitContent(createDefaultDocumentLayout()));
                  setSelectedId(null);
                }}
                title="Auf Standard-Layout zurücksetzen"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Zurücksetzen
              </button>
            </div>

            <div className="ml-auto flex shrink-0 items-center gap-2 pl-2">
              {error ? (
                <span className="max-w-[320px] text-xs leading-snug text-red-600" title={error}>
                  {error}
                </span>
              ) : validationWarning ? (
                <span
                  className="max-w-[280px] text-xs leading-snug text-amber-700"
                  title={layoutValidation.issues.map((i) => i.message).join('\n')}
                >
                  {validationWarning}
                </span>
              ) : null}
              {showSavedHint && !isDirty ? (
                <span className="text-xs font-medium text-emerald-600">Gespeichert</span>
              ) : null}
              <button
                type="button"
                className={SETTINGS_BTN_DANGER_OUTLINE}
                disabled={saving || deleting}
                onClick={() => void onDelete()}
                title="Vorlage löschen"
              >
                {deleting ? (
                  <LoadingSpinner />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Löschen
              </button>
              <button
                type="button"
                className={
                  isDirty
                    ? SETTINGS_BTN_PRIMARY
                    : `${SETTINGS_BTN_OUTLINE} text-app-text-subtle`
                }
                disabled={saving || deleting || !isDirty}
                onClick={() => void onSave()}
              >
                {saving ? <LoadingSpinner /> : null}
                Speichern
              </button>
            </div>
          </div>

          <LayoutCanvas
            layout={layout}
            activeBand={activeBand}
            selectedId={selectedId}
            multiSelectIds={multiSelectIds}
            zoom={zoom}
            showGuides={showGuides}
            previewMode={previewMode}
            logoUrl={logoUrl}
            assetUrls={assetUrls}
            sampleData={sampleData}
            introParas={introParas}
            onZoomNudge={nudgeZoom}
            onBandChange={setActiveBand}
            onSelect={(id, opts) => {
              if (id) setShowPageSettings(false);
              if (opts?.shift && id) {
                setMultiSelectIds((prev) => {
                  const base =
                    prev.length === 0 && selectedId && selectedId !== id
                      ? [selectedId]
                      : prev;
                  return base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
                });
                setSelectedId(null);
                return;
              }
              setMultiSelectIds([]);
              setSelectedId(id);
              setContextMenu(null);
            }}
            onMarqueeSelect={(ids) => {
              setContextMenu(null);
              if (ids.length > 0) setShowPageSettings(false);
              if (ids.length === 0) {
                setSelectedId(null);
                setMultiSelectIds([]);
                return;
              }
              if (ids.length === 1) {
                setSelectedId(ids[0]);
                setMultiSelectIds([]);
                return;
              }
              setSelectedId(null);
              setMultiSelectIds(ids);
            }}
            onCommitElements={commitBandElements}
            onCommitBandHeight={commitBandHeight}
            onDropPalette={onDropPalette}
            onContextMenu={(id, x, y) => setContextMenu({ id, x, y })}
          />
        </div>

        {showPageSettings ? (
          <PageSettingsPanel
            layout={layout}
            onChange={(patch) =>
              commit({
                ...layout,
                body: {
                  ...layout.body,
                  ...patch,
                },
              })
            }
          />
        ) : (
          <LayoutInspector
            element={selectedEl}
            band={selectedBand}
            bandHeightPt={
              selectedBand === 'footer' ? layout.footer.heightPt : layout.header.heightPt
            }
            assetPreviewUrl={selectedEl ? assetUrls[selectedEl.id] ?? null : null}
            assetUploading={assetUploading}
            onUploadAsset={async (file) => {
              if (!selectedEl || !selectedBand) return;
              if (selectedEl.key !== 'headerBanner' && selectedEl.key !== 'signatureImage') return;
              setAssetUploading(true);
              setError(null);
              try {
                const kind = selectedEl.key === 'headerBanner' ? 'banner' : 'signature';
                const objectPath = await uploadDocumentLayoutAsset(template.id, kind, file);
                patchSelected({ imageObjectPath: objectPath });
                setAssetBust(Date.now());
              } catch (e) {
                setError(e instanceof Error ? e.message : 'Bild-Upload fehlgeschlagen.');
              } finally {
                setAssetUploading(false);
              }
            }}
            onClearAsset={() => {
              if (!selectedEl) return;
              patchSelected({ imageObjectPath: '' });
              setAssetBust(Date.now());
            }}
            onSnapRecipientToDinWindow={() => {
              if (!selectedId || selectedEl?.key !== 'recipient') return;
              commit(snapRecipientToDinAddressWindow(layout, selectedId));
            }}
            onChange={patchSelected}
            onLock={() => patchSelected({ locked: !selectedEl?.locked })}
            canRemove={
              !!selectedId && !!selectedBand && canRemoveElement(layout, selectedBand, selectedId)
            }
            onRemove={() => {
              if (!selectedId || !selectedBand) return;
              if (!canRemoveElement(layout, selectedBand, selectedId)) return;
              commit(removeElementFromLayout(layout, selectedBand, selectedId));
              setSelectedId(null);
            }}
          />
        )}
      </div>

      {contextMenu ? (
        <div
          className="fixed z-[2000] min-w-[160px] rounded-md border border-app-border bg-app-surface p-1 shadow-lg"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          {(
            [
              ['dup', 'Duplizieren'],
              ['lock', 'Sperren/Entsperren'],
              ['del', 'Löschen'],
            ] as const
          ).map(([action, label]) => (
            <button
              key={action}
              type="button"
              className={`block w-full rounded-md px-2.5 py-1.5 text-left text-sm hover:bg-app-hover ${
                action === 'del' ? 'text-red-600' : 'text-app-text-muted'
              }`}
              onClick={() => {
                const band = findElementBand(layout, contextMenu.id);
                const el =
                  band &&
                  (band === 'header' ? layout.header.elements : layout.footer.elements).find(
                    (e) => e.id === contextMenu.id,
                  );
                if (!band || !el) {
                  setContextMenu(null);
                  return;
                }
                if (action === 'dup') {
                  const bandHeight =
                    band === 'header' ? layout.header.heightPt : layout.footer.heightPt;
                  const copy = cloneOffsetElement(el, bandHeight);
                  const next = structuredClone(layout);
                  if (band === 'header') next.header.elements.push(copy);
                  else next.footer.elements.push(copy);
                  commit(next);
                  setSelectedId(copy.id);
                } else if (action === 'lock') {
                  commit(updateElementInLayout(layout, band, el.id, { locked: !el.locked }));
                } else if (action === 'del' && canRemoveElement(layout, band, el.id)) {
                  commit(removeElementFromLayout(layout, band, el.id));
                  setSelectedId(null);
                }
                setContextMenu(null);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
